/**
 * SponsorsFullScreen — full-height overlay for sponsors.
 * Same pattern as EventsFullScreen: fixed overlay on top of map.
 */

import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandHoldingHeart } from "@fortawesome/pro-solid-svg-icons/faHandHoldingHeart";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import type { SponsorTierGroup } from "@wondermaps/db";
import type { SponsorLogo } from "./sponsor-types";
import { SponsorsBody } from "./SponsorsBottomSheet";

export interface SponsorsFullScreenProps {
  sponsors: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  getPoiName?: (poiId: string) => string | undefined;
  onPoiClick?: (poiId: string) => void;
  onClose: () => void;
}

export function SponsorsFullScreen({
  sponsors,
  onSponsorClick,
  getPoiName,
  onPoiClick,
  onClose,
}: SponsorsFullScreenProps) {
  const { t } = useTranslation("map");

  return (
    <div className="fixed inset-0 z-30 bg-background flex flex-col" role="dialog" aria-modal="true" aria-label={t("menu.ourSponsors")}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-4 py-3 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
          aria-label={t("sponsors.close")}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
        </button>
        <FontAwesomeIcon icon={faHandHoldingHeart} className="h-5 w-5 text-muted-foreground" />
        <h2 className="font-semibold text-lg flex-1">{t("menu.ourSponsors")}</h2>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <SponsorsBody
          sponsors={sponsors}
          onSponsorClick={onSponsorClick}
          getPoiName={getPoiName}
          onPoiClick={onPoiClick}
          placement="sponsors_fullscreen"
        />
      </div>
    </div>
  );
}
